import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  GridCellEditStartParams,
  GridCellParams,
  useGridApiRef,
} from "@mui/x-data-grid";
import {
  ArrowDownward,
  ArrowUpward,
  Delete as DeleteIcon,
  Download,
  Refresh,
  Save,
} from "@mui/icons-material";

import { ParamExUr } from "./Consultation/types";
import { normalizeParamExUrList } from "./utils/paramExUrTemplate";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5000/api";

const DEFAULT_ENDPOINTS = {
  paramExUrEmpty: "/ParamExUrModel",
};

const resolveApiUrl = (baseUrl: string, pathOrUrl: string) => {
  const value = (pathOrUrl || "").trim();
  if (!value) return baseUrl;
  if (/^https?:\/\//i.test(value)) return value;
  const normalizedBase = baseUrl.replace(/\/+$/, "");
  const normalizedPath = value.startsWith("/") ? value : `/${value}`;
  return `${normalizedBase}${normalizedPath}`;
};

const getApiHeaders = () => ({
  Accept: "application/json",
  Authorization: `Bearer ${localStorage.getItem("authToken")}`,
  "X-API-Key": process.env.REACT_APP_API_KEY || "",
});

const createEmptyRow = (pozitie: number): ParamExUr => ({
  idCons: 0,
  idParam: 0,
  denumire: "",
  valoare: "",
  pozitie,
  tip: 0,
  valoareMinimala: "",
  valoareMaximala: "",
});

const extractListFromResponse = (data: any): unknown => {
  return (
    (Array.isArray(data) && data) ||
    data?.ParamExUrList ||
    data?.paramExUrList ||
    data?.ParamExUr ||
    data?.paramExUr ||
    []
  );
};

const ParamExUrTemplatePage: React.FC = () => {
  const apiRef = useGridApiRef();
  const [rows, setRows] = useState<ParamExUr[]>([]);
  const [status, setStatus] = useState<
    "idle" | "loading" | "saving" | "saved" | "error"
  >("idle");
  const [message, setMessage] = useState<string>("");

  const log = useCallback(
    (...args: any[]) => console.log("[ParamExUrTemplatePage]", ...args),
    [],
  );
  const warn = useCallback(
    (...args: any[]) => console.warn("[ParamExUrTemplatePage]", ...args),
    [],
  );
  const error = useCallback(
    (...args: any[]) => console.error("[ParamExUrTemplatePage]", ...args),
    [],
  );

  const normalizedRows = useMemo(() => normalizeParamExUrList(rows), [rows]);

  const scrollCellIntoView = (id: any, field?: string) => {
    try {
      const api: any = apiRef.current;
      if (!api) return;

      const rowIndex: number | null =
        typeof api.getRowIndexRelativeToVisibleRows === "function"
          ? api.getRowIndexRelativeToVisibleRows(id)
          : ((api.getRow?.(id) as any)?.rowIndex ?? null);

      if (rowIndex === null || rowIndex === undefined) return;

      let colIndex = 0;
      if (field && typeof api.getAllColumns === "function") {
        const cols = api.getAllColumns();
        const idx = cols.findIndex((c: any) => c?.field === field);
        if (idx >= 0) colIndex = idx;
      }

      if (typeof api.scrollToIndexes === "function") {
        api.scrollToIndexes({ rowIndex, colIndex });
      }
    } catch {
      // best-effort scrolling; ignore
    }
  };

  const loadFromServer = useCallback(async () => {
    setStatus("loading");
    setMessage("");

    const configured =
      process.env.REACT_APP_CONSULTATION_PARAM_EXUR_EMPTY_ENDPOINT ||
      DEFAULT_ENDPOINTS.paramExUrEmpty;
    const url = resolveApiUrl(API_BASE_URL, configured);

    log("Loading template", {
      apiBaseUrl: API_BASE_URL,
      configuredEndpoint: configured,
      url,
      hasAuthToken: !!localStorage.getItem("authToken"),
    });

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: getApiHeaders(),
      });

      log("Load response", { status: response.status, ok: response.ok });

      if (!response.ok) {
        const text = await response.text().catch(() => "");
        warn("Load failed", { status: response.status, body: text });
        throw new Error(`HTTP ${response.status}: ${text}`);
      }

      const data: any = await response.json().catch(() => null);
      const listCandidate = extractListFromResponse(data);

      const normalized = normalizeParamExUrList(listCandidate);
      log("Template loaded", {
        receivedType: Array.isArray(listCandidate)
          ? "array"
          : typeof listCandidate,
        count: normalized.length,
      });
      setRows(normalized);
      setStatus("idle");
      setMessage(`Template încărcat (${normalized.length} rânduri).`);
    } catch (e: any) {
      error("Load error", e);
      setStatus("error");
      setMessage(String(e?.message ?? e ?? "Load failed"));
    }
  }, [error, log, warn]);

  const saveToServer = async (list: ParamExUr[]) => {
    setStatus("saving");
    setMessage("");

    const configuredSave =
      process.env.REACT_APP_PARAM_EXUR_TEMPLATE_SAVE_ENDPOINT ||
      DEFAULT_ENDPOINTS.paramExUrEmpty;
    const url = resolveApiUrl(API_BASE_URL, configuredSave);

    const payload = normalizeParamExUrList(list);

    log("Saving template", {
      apiBaseUrl: API_BASE_URL,
      configuredEndpoint: configuredSave,
      url,
      count: payload.length,
      hasAuthToken: !!localStorage.getItem("authToken"),
    });

    const tryRequest = async (
      method: "POST" | "PUT",
      body: any,
    ): Promise<Response> => {
      return fetch(url, {
        method,
        headers: {
          ...getApiHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
    };

    const attempt = async (method: "POST" | "PUT") => {
      log("Save attempt", { method });
      let response = await tryRequest(method, payload);
      if (
        !response.ok &&
        (response.status === 400 || response.status === 415)
      ) {
        warn("Save attempt failed; retrying with wrapped body", {
          method,
          status: response.status,
        });
        response = await tryRequest(method, { paramExUrList: payload });
      }
      return response;
    };

    try {
      let response = await attempt("POST");
      if (response.status === 405) {
        warn("POST not allowed; retrying with PUT", {
          status: response.status,
        });
        response = await attempt("PUT");
      }

      if (!response.ok) {
        const text = await response.text().catch(() => "");
        warn("Save failed", { status: response.status, body: text });
        throw new Error(`HTTP ${response.status}: ${text}`);
      }

      log("Template saved", { count: payload.length });
      setRows(payload);
      setStatus("saved");
      setMessage("Template salvat pe server.");
      window.setTimeout(() => setStatus("idle"), 1000);
    } catch (e: any) {
      error("Save error", e);
      setStatus("error");
      setMessage(String(e?.message ?? e ?? "Save failed"));
    }
  };

  useEffect(() => {
    log("Mounted", {
      apiBaseUrl: API_BASE_URL,
      defaultEndpoint: DEFAULT_ENDPOINTS.paramExUrEmpty,
      envLoadEndpoint: process.env.REACT_APP_PARAM_EXUR_MODEL_ENDPOINT,
      envSaveEndpoint: process.env.REACT_APP_PARAM_EXUR_TEMPLATE_SAVE_ENDPOINT,
    });
    void loadFromServer();
  }, [loadFromServer, log]);

  const addRow = () => {
    const nextPoz = (normalizedRows?.length ?? 0) + 1;
    log("Add row", { nextPoz, beforeCount: normalizedRows.length });
    setRows((prev) =>
      normalizeParamExUrList([...prev, createEmptyRow(nextPoz)]),
    );
  };

  const deleteRow = (index: number) => {
    log("Delete row", { index, beforeCount: normalizedRows.length });
    setRows((prev) =>
      normalizeParamExUrList(prev.filter((_, i) => i !== index)),
    );
  };

  const moveRow = (index: number, direction: -1 | 1) => {
    log("Move row", { index, direction, beforeCount: normalizedRows.length });
    setRows((prev) => {
      const normalized = normalizeParamExUrList(prev);
      const target = index + direction;
      if (target < 0 || target >= normalized.length) return normalized;
      const copy = [...normalized];
      const tmp = copy[index];
      copy[index] = copy[target];
      copy[target] = tmp;
      return normalizeParamExUrList(copy);
    });
  };

  const save = () => {
    log("Save clicked", { count: normalizedRows.length });
    void saveToServer(normalizedRows);
  };

  const clear = () => {
    log("Clear clicked");
    void saveToServer([]);
  };

  const exportJson = () => {
    try {
      log("Copy JSON clicked", { count: normalizedRows.length });
      const payload = JSON.stringify(normalizedRows, null, 2);
      navigator.clipboard
        .writeText(payload)
        .then(() => setMessage("JSON copiat în clipboard."))
        .catch(() => setMessage(payload));
    } catch {
      // ignore
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Parametri Eco — Template
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Editează lista implicită folosită la consulturi noi.
          </Typography>
        </Box>

        {status === "error" && <Alert severity="error">{message}</Alert>}
        {status !== "error" && message && (
          <Alert severity="info">{message}</Alert>
        )}

        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={save}
            disabled={status === "saving" || status === "loading"}
          >
            Save
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => void loadFromServer()}
            disabled={status === "saving" || status === "loading"}
          >
            Load
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={exportJson}
          >
            Copy JSON
          </Button>
          <Button
            variant="text"
            color="error"
            onClick={clear}
            disabled={status === "saving" || status === "loading"}
          >
            Clear
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button variant="outlined" onClick={addRow}>
            Add Row
          </Button>
        </Stack>

        <Divider />

        <Paper sx={{ height: 520, width: "100%" }}>
          <DataGrid
            apiRef={apiRef}
            rows={normalizedRows.map((r, index) => ({
              id: r.idParam || `tmp-${index}`,
              ...r,
              rowIndex: index,
            }))}
            columns={[
              {
                field: "pozitie",
                headerName: "Nr.",
                width: 70,
                editable: false,
                align: "center",
                headerAlign: "center",
              },
              {
                field: "denumire",
                headerName: "Denumire Parametru",
                width: 260,
                editable: true,
              },
              {
                field: "tip",
                headerName: "Tip",
                width: 90,
                editable: true,
                type: "number",
                align: "center",
                headerAlign: "center",
              },
              {
                field: "valoare",
                headerName: "Valoare",
                width: 220,
                editable: true,
              },
              {
                field: "valoareMinimala",
                headerName: "Valoare Min.",
                width: 160,
                editable: true,
              },
              {
                field: "valoareMaximala",
                headerName: "Valoare Max.",
                width: 160,
                editable: true,
              },
              {
                field: "actions",
                headerName: "Acțiuni",
                width: 160,
                sortable: false,
                filterable: false,
                renderCell: (params) => (
                  <Stack direction="row" spacing={0.5}>
                    <IconButton
                      size="small"
                      title="Mută sus"
                      onClick={() => moveRow(params.row.rowIndex, -1)}
                    >
                      <ArrowUpward fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      title="Mută jos"
                      onClick={() => moveRow(params.row.rowIndex, 1)}
                    >
                      <ArrowDownward fontSize="small" />
                    </IconButton>
                    <IconButton
                      color="error"
                      size="small"
                      title="Șterge"
                      onClick={() => deleteRow(params.row.rowIndex)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ),
              },
            ]}
            processRowUpdate={(newRow, oldRow) => {
              const idx = Number(oldRow?.rowIndex);
              if (Number.isFinite(idx) && idx >= 0) {
                const changed: string[] = [];
                const fields: Array<keyof typeof newRow> = [
                  "denumire",
                  "valoare",
                  "tip",
                  "valoareMinimala",
                  "valoareMaximala",
                ];
                for (const f of fields) {
                  if ((newRow as any)?.[f] !== (oldRow as any)?.[f])
                    changed.push(String(f));
                }
                if (changed.length) {
                  console.log("[ParamExUrTemplatePage] Row edited", {
                    index: idx,
                    changed,
                  });
                }
                setRows((prev) => {
                  const normalized = normalizeParamExUrList(prev);
                  if (idx >= normalized.length) return normalized;
                  const updated = [...normalized];
                  updated[idx] = {
                    ...updated[idx],
                    denumire: String(newRow.denumire ?? ""),
                    valoare: String(newRow.valoare ?? ""),
                    tip: Number(newRow.tip ?? 0),
                    valoareMinimala: String(newRow.valoareMinimala ?? ""),
                    valoareMaximala: String(newRow.valoareMaximala ?? ""),
                  };
                  return normalizeParamExUrList(updated);
                });
              }
              return newRow;
            }}
            onCellEditStart={(params: GridCellEditStartParams) => {
              scrollCellIntoView(params.id, params.field);
            }}
            onCellClick={(params: GridCellParams) => {
              scrollCellIntoView(params.id, params.field);
            }}
            onProcessRowUpdateError={(error) => {
              console.error("[ParamExUrTemplatePage] Row update error:", error);
            }}
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            disableRowSelectionOnClick
            sx={{
              "& .MuiDataGrid-cell:focus": {
                outline: "none",
              },
              "& .MuiDataGrid-cell:focus-within": {
                outline: "2px solid",
                outlineColor: "primary.main",
              },
            }}
          />
        </Paper>
      </Stack>
    </Box>
  );
};

export default ParamExUrTemplatePage;
